import React, { useEffect, useMemo, useRef, useState } from "react";
import { adminApi, buildQs, SOURCE_META, STATUS_META, srcLabel } from "./api";
import LeadDetail from "./LeadDetail";
import {
  Search, Download, Loader2, ChevronDown, ChevronUp, Trash2, Send, Tag as TagIcon,
  X, ChevronLeft, ChevronRight, SlidersHorizontal,
} from "lucide-react";

const STATUSES = Object.keys(STATUS_META);
const SORTABLE = [
  { key: "created_at", label: "Date" }, { key: "name", label: "Name" },
  { key: "source", label: "Source" }, { key: "city", label: "City" },
  { key: "grade", label: "Grade" }, { key: "status", label: "Status" },
];

const OPT_COLS = [
  { key: "email", label: "Email" }, { key: "phone", label: "Phone" },
  { key: "grade", label: "Grade" }, { key: "assigned", label: "Assigned" },
];

export default function LeadsTable({ initialFilters = {}, deletedBin = false }) {
  const [filters, setFilters] = useState({ source: "", status: "", city: "", grade: "", assigned: "", date_from: "", date_to: "", tag: "" });
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [shareIds, setShareIds] = useState(null);
  const [delIds, setDelIds] = useState(null);
  const [cols, setCols] = useState({ email: true, phone: true, grade: true, assigned: true });
  const [colsOpen, setColsOpen] = useState(false);
  const searchTimer = useRef(null);
  const pageSize = 50;

  useEffect(() => {
    if (initialFilters.source || initialFilters.city || initialFilters.grade || initialFilters.status) {
      setFilters((f) => ({ ...f, source: initialFilters.source || "", city: initialFilters.city || "", grade: initialFilters.grade || "", status: initialFilters.status || "" }));
    }
    // eslint-disable-next-line
  }, []);

  const load = () => {
    setLoading(true);
    const assigned = filters.assigned === "__none__" ? "" : filters.assigned;
    const qs = buildQs({ ...filters, assigned, q, sort, order, page, page_size: pageSize, deleted: deletedBin });
    adminApi.leads(qs).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, q, sort, order, page, deletedBin]);

  const items = data?.items || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const toggleSort = (key) => {
    if (sort === key) setOrder((o) => (o === "desc" ? "asc" : "desc"));
    else { setSort(key); setOrder(key === "name" || key === "city" ? "asc" : "desc"); }
  };
  const onSearch = (v) => { setQ(v); setPage(1); };
  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const allChecked = items.length > 0 && selected.length === items.length;
  const toggleAll = () => setSelected(allChecked ? [] : items.map((i) => i.lead_id));
  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const exportCurrent = () => {
    const assigned = filters.assigned === "__none__" ? "" : filters.assigned;
    const qs = buildQs({ ...filters, assigned, q, sort, order, deleted: deletedBin });
    window.open(adminApi.exportUrl(qs), "_blank");
  };

  const bulkStatus = async (s) => { await adminApi.bulkStatus(selected, s); setSelected([]); load(); };
  const bulkTag = async (t) => { if (!t) return; await adminApi.bulkTag(selected, t); setSelected([]); load(); };
  const restoreSel = async () => { await adminApi.restore(selected); setSelected([]); load(); };

  return (
    <div className="space-y-4" data-testid="leads-table">
      {/* Search + tools */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input defaultValue={q} onChange={(e) => { clearTimeout(searchTimer.current); const v = e.target.value; searchTimer.current = setTimeout(() => onSearch(v), 300); }}
            placeholder="Search name, email, phone, city, company, school…" data-testid="leads-search"
            className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-[14px]" />
        </div>
        <button onClick={exportCurrent} data-testid="export-btn" className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"><Download className="h-4 w-4" /> Export CSV</button>
        <div className="relative">
          <button onClick={() => setColsOpen((v) => !v)} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[13px] font-semibold text-slate-700 hover:bg-slate-50"><SlidersHorizontal className="h-4 w-4" /> Columns</button>
          {colsOpen && (
            <div className="absolute right-0 mt-1 z-20 w-40 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
              {OPT_COLS.map((c) => (
                <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 text-[13px] cursor-pointer hover:bg-slate-50 rounded-lg">
                  <input type="checkbox" checked={cols[c.key]} onChange={() => setCols((s) => ({ ...s, [c.key]: !s[c.key] }))} /> {c.label}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <select value={filters.source} onChange={(e) => setF("source", e.target.value)} data-testid="filter-source" className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[13px]">
          <option value="">All sources</option>
          {Object.entries(SOURCE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => setF("status", e.target.value)} data-testid="filter-status" className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[13px]">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input value={filters.city} onChange={(e) => setF("city", e.target.value)} placeholder="City" className="w-28 rounded-lg border border-slate-200 px-2.5 py-2 text-[13px]" />
        <input value={filters.grade} onChange={(e) => setF("grade", e.target.value)} placeholder="Grade" className="w-20 rounded-lg border border-slate-200 px-2.5 py-2 text-[13px]" />
        <input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} className="rounded-lg border border-slate-200 px-2.5 py-2 text-[13px]" />
        <input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} className="rounded-lg border border-slate-200 px-2.5 py-2 text-[13px]" />
        {(filters.source || filters.status || filters.city || filters.grade || filters.date_from || filters.date_to || filters.assigned || filters.tag) && (
          <button onClick={() => setFilters({ source: "", status: "", city: "", grade: "", assigned: "", date_from: "", date_to: "", tag: "" })} className="inline-flex items-center gap-1 text-[13px] font-semibold text-slate-500 px-2"><X className="h-3.5 w-3.5" /> Clear</button>
        )}
      </div>

      {/* Bulk actions */}
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-slate-900 text-white px-4 py-2.5" data-testid="bulk-bar">
          <span className="text-[13px] font-semibold">{selected.length} selected</span>
          <select onChange={(e) => { if (e.target.value) bulkStatus(e.target.value); e.target.value = ""; }} className="rounded-lg bg-slate-800 border border-slate-700 px-2 py-1.5 text-[12px]">
            <option value="">Set status…</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input placeholder="Add tag + Enter" onKeyDown={(e) => { if (e.key === "Enter") { bulkTag(e.target.value); e.target.value = ""; } }} className="w-32 rounded-lg bg-slate-800 border border-slate-700 px-2 py-1.5 text-[12px]" />
          {!deletedBin && <button onClick={() => setShareIds(selected)} className="inline-flex items-center gap-1 rounded-lg bg-white/10 px-2.5 py-1.5 text-[12px] font-semibold hover:bg-white/20"><Send className="h-3.5 w-3.5" /> Share</button>}
          {deletedBin
            ? <button onClick={restoreSel} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-2.5 py-1.5 text-[12px] font-semibold">Restore</button>
            : <button onClick={() => setDelIds(selected)} data-testid="bulk-delete" className="inline-flex items-center gap-1 rounded-lg bg-rose-500 px-2.5 py-1.5 text-[12px] font-semibold hover:bg-rose-600"><Trash2 className="h-3.5 w-3.5" /> Delete</button>}
          <button onClick={() => setSelected([])} className="ml-auto text-[12px] text-slate-300 hover:text-white">Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-[12px] font-bold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-3 w-10"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="select-all" /></th>
                {[["created_at", "Date"], ["source", "Source"], ["name", "Name"], ["city", "City"]].map(([k, l]) => (
                  <Th key={k} k={k} label={l} sort={sort} order={order} onClick={toggleSort} />
                ))}
                {cols.email && <th className="px-3 py-3">Email</th>}
                {cols.phone && <th className="px-3 py-3">Phone</th>}
                {cols.grade && <Th k="grade" label="Grade" sort={sort} order={order} onClick={toggleSort} />}
                <Th k="status" label="Status" sort={sort} order={order} onClick={toggleSort} />
                {cols.assigned && <th className="px-3 py-3">Assigned</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={10} className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-violet-500 inline" /></td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={10} className="py-16 text-center text-slate-400 text-[14px]">No leads match these filters.</td></tr>}
              {!loading && items.map((l) => {
                const meta = SOURCE_META[l.source] || {};
                return (
                  <tr key={l.lead_id} className="hover:bg-violet-50/40 cursor-pointer text-[13px]" onClick={() => setDetailId(l.lead_id)} data-testid={`lead-row-${l.lead_id}`}>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.includes(l.lead_id)} onChange={() => toggleOne(l.lead_id)} data-testid={`select-${l.lead_id}`} /></td>
                    <td className="px-3 py-3 whitespace-nowrap text-slate-500">{(l.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${meta.chip || "bg-slate-200 text-slate-700"}`}>{srcLabel(l.source)}</span></td>
                    <td className="px-3 py-3 font-semibold text-slate-800 whitespace-nowrap">{l.name || "—"}</td>
                    <td className="px-3 py-3 text-slate-600">{l.city || "—"}</td>
                    {cols.email && <td className="px-3 py-3 text-slate-600">{l.email || "—"}</td>}
                    {cols.phone && <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{l.phone || "—"}</td>}
                    {cols.grade && <td className="px-3 py-3 text-slate-600">{l.grade || "—"}</td>}
                    <td className="px-3 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_META[l.status] || "bg-slate-100 text-slate-600"}`}>{l.status}</span></td>
                    {cols.assigned && <td className="px-3 py-3 text-slate-600">{l.assigned_to || "—"}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-[13px] text-slate-500">
        <span>{total} leads · page {page} of {pages}</span>
        <div className="flex gap-1">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="inline-flex items-center rounded-lg border border-slate-200 px-2 py-1.5 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="inline-flex items-center rounded-lg border border-slate-200 px-2 py-1.5 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      {detailId && <LeadDetail leadId={detailId} statuses={STATUSES} onClose={() => setDetailId(null)} onChanged={load} onShare={(ids) => setShareIds(ids)} />}
      {shareIds && <ShareModal ids={shareIds} onClose={() => setShareIds(null)} />}
      {delIds && <DeleteModal ids={delIds} onClose={() => setDelIds(null)} onDone={() => { setDelIds(null); setSelected([]); load(); }} />}
    </div>
  );
}

const Th = ({ k, label, sort, order, onClick }) => (
  <th className="px-3 py-3 cursor-pointer select-none" onClick={() => onClick(k)}>
    <span className="inline-flex items-center gap-1">{label}{sort === k && (order === "desc" ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />)}</span>
  </th>
);

function ShareModal({ ids, onClose }) {
  const [recipients, setRecipients] = useState("");
  const [note, setNote] = useState("");
  const [incNotes, setIncNotes] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await adminApi.share({ lead_ids: ids, recipients, note, include_internal_notes: incNotes }); setDone(true); }
    catch (e) { alert("Could not send share."); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4" data-testid="share-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-extrabold text-slate-900">Share {ids.length} lead{ids.length > 1 ? "s" : ""}</h3>
        {done ? (
          <div className="mt-4"><p className="text-[14px] text-emerald-600 font-semibold">Sent successfully.</p>
            <button onClick={onClose} className="mt-4 rounded-lg bg-slate-900 text-white px-4 py-2 text-[13px] font-semibold">Close</button></div>
        ) : (
          <>
            <p className="mt-1 text-[13px] text-slate-500">Recipients can be anyone — no login needed. Core fields only unless you include internal notes.</p>
            <label className="block mt-4 text-[12px] font-semibold text-slate-500">Recipient email(s), comma-separated</label>
            <input value={recipients} onChange={(e) => setRecipients(e.target.value)} data-testid="share-recipients" placeholder="a@x.com, b@y.com" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[14px]" />
            <label className="block mt-3 text-[12px] font-semibold text-slate-500">Optional note</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[14px]" />
            <label className="mt-3 flex items-center gap-2 text-[13px] text-slate-700"><input type="checkbox" checked={incNotes} onChange={() => setIncNotes((v) => !v)} /> Include internal notes (off by default)</label>
            <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 p-2.5 text-[12px] text-slate-500">Will send: Name, Email, Phone, City, Source, Date, Status{incNotes ? ", internal notes" : ""}. Never includes Discover scores.</div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-[13px] font-semibold">Cancel</button>
              <button onClick={submit} disabled={busy || !recipients.trim()} data-testid="share-send" className="rounded-lg bg-slate-900 text-white px-4 py-2 text-[13px] font-semibold disabled:opacity-50">{busy ? "Sending…" : "Send"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DeleteModal({ ids, onClose, onDone }) {
  const many = ids.length > 1;
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = !many || confirm === "DELETE";
  const submit = async () => { setBusy(true); await adminApi.del(ids); setBusy(false); onDone(); };
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4" data-testid="delete-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-extrabold text-slate-900">Delete {ids.length} lead{many ? "s" : ""}?</h3>
        <p className="mt-1 text-[13px] text-slate-500">They move to the Deleted bin (recoverable for 30 days).</p>
        {many && (
          <>
            <label className="block mt-4 text-[12px] font-semibold text-slate-500">Type DELETE to confirm</label>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="delete-confirm-input" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[14px]" />
          </>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-[13px] font-semibold">Cancel</button>
          <button onClick={submit} disabled={!ok || busy} data-testid="delete-confirm-btn" className="rounded-lg bg-rose-600 text-white px-4 py-2 text-[13px] font-semibold disabled:opacity-50">Delete</button>
        </div>
      </div>
    </div>
  );
}
